import { useState, useRef, useEffect } from 'react'
import { Cog6ToothIcon, ArrowRightOnRectangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'

interface UserDropdownMenuProps {
  /** Optional additional CSS classes for the container */
  className?: string
  /** Display mode: 'full' shows email and avatar, 'compact' shows only avatar */
  mode?: 'full' | 'compact'
}

/**
 * Shared user dropdown menu component providing:
 * - Account Settings action (future functionality)
 * - Logout action (flows through useAuth().signOut())
 *
 * Used in both SongList header and AppHeader for consistent UX.
 * Battle-tested pattern: dropdown with click-outside-to-close behavior.
 */
export function UserDropdownMenu({ className = '', mode = 'full' }: UserDropdownMenuProps) {
  const { user, signOut } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside - classic pattern from the trenches
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleLogout = async () => {
    setIsOpen(false)
    const { error } = await signOut()
    if (error) {
      console.error('Logout failed:', error)
      // User will be redirected to login anyway due to auth state change
    }
  }

  const handleAccountSettings = () => {
    setIsOpen(false)
    // TODO: Navigate to account settings page when implemented
    console.log('Account settings clicked - feature coming soon')
  }

  // Extract display info from user email
  const displayName = user?.email?.split('@')[0] || 'User'
  const userInitial = (user?.email?.[0] || 'U').toUpperCase()

  if (mode === 'compact') {
    // Compact mode: just the avatar with dropdown
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium hover:bg-blue-600 transition-colors duration-200"
          aria-label="User menu"
        >
          {userInitial}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleAccountSettings}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors duration-150"
            >
              <Cog6ToothIcon className="w-4 h-4" />
              <span>Account Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-150"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    )
  }

  // Full mode: email + avatar + chevron indicator
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-200 border border-white/10 hover:border-white/20"
        aria-label="User menu"
      >
        <div className="text-right hidden sm:block">
          <p className="text-sm font-medium text-white">{displayName}</p>
          <p className="text-xs text-neutral-300">{user?.email}</p>
        </div>
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {userInitial}
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 text-neutral-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleAccountSettings}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors duration-150"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            <span>Account Settings</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-150"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  )
}
